import { useEventListener } from '@vueuse/core';
import { parse } from 'qs';
import { stringifyQueryString } from '~/utils/string';
import { pausableWatch } from '@vueuse/shared';
import { reactive } from 'vue';

function isObject(val) {
    return val.constructor === Object;
}

function isNumber(val) {
    return !isNaN(parseFloat(val)) && isFinite(val);
}

function isBoolean(val) {
    return val === 'false' || val === 'true';
}

function isArray(val) {
    return Array.isArray(val);
}

function parseValue(val) {
    if (typeof val == 'undefined' || val == '') {
        return null;
    } else if (isBoolean(val)) {
        return parseBoolean(val);
    } else if (isArray(val)) {
        return parseArray(val);
    } else if (isObject(val)) {
        return parseObject(val);
    } else if (isNumber(val)) {
        return parseNumber(val);
    } else {
        return val;
    }
}

function parseObject(obj) {
    var result = {};
    var key, val;
    for (key in obj) {
        val = parseValue(obj[key]);
        if (val !== null) result[key] = val; // ignore null values
    }
    return result;
}

function parseArray(arr) {
    var result = [];
    for (var i = 0; i < arr.length; i++) {
        result[i] = parseValue(arr[i]);
    }
    return result;
}

function parseNumber(val) {
    return Number(val);
}

function parseBoolean(val) {
    return val === 'true';
}

// code adapted from https://github.com/vueuse/vueuse/blob/main/packages/core/useUrlSearchParams/index.ts
// this matches php style for query params using `qs` package
// removed hash logic
export function useQueryParams(options = {}) {
    const { initialValue = {}, write: enableWrite = true } = options;

    const state = reactive({});

    function getRawParams() {
        const rawParams = window.location.search || '';
        return rawParams.startsWith('?') ? rawParams.slice(1) : rawParams;
    }

    function constructQuery(params) {
        return stringifyQueryString(params);
    }

    function read() {
        const rawParams = getRawParams();

        return parseObject(rawParams ? parse(rawParams, {}) : initialValue);
    }

    function updateState(params) {
        Object.assign(state, JSON.parse(JSON.stringify(params)));
    }

    const { pause, resume } = pausableWatch(
        state,
        () => {
            write(JSON.parse(JSON.stringify(state)));
        },
        { deep: true }
    );

    function write(params, shouldUpdate = undefined) {
        pause();

        if (shouldUpdate) updateState(params);

        window.history.replaceState(
            window.history.state,
            window.document.title,
            window.location.pathname + '?' + constructQuery(params)
        );

        resume();
    }

    function onChanged() {
        if (!enableWrite) return;

        write(read(), true);
    }

    useEventListener(window, 'popstate', onChanged, false);

    const initial = read();
    Object.assign(state, initial);

    return state;
}
